import { ref, reactive } from 'vue';
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import { CUSTOMER_STATUS } from '@/utils/enum';

export default function useCustomerList() {
  const customers = ref([]);
  const totalItems = ref(0);
  const totalPages = ref(0);
  const currentPage = ref(1);

  const isLoading = ref(false);

  const filters = reactive({
    name: '',
    taxCode: '',
    phone: '',
    dateOfBirth: '',
    gender: null,
    status: null,
    type: null,
    parentId: null,
    parentName: '',
    id: null,
    queryBy: null,
  });

  const loadCustomers = async (page = 1) => {
    isLoading.value = true;
    currentPage.value = page;
    try {
      const params = {
        customerName: filters.name.trim(),
        customerTaxCode: filters.taxCode.trim(),
        customerPhone: filters.phone.trim(),
        customerDOB: filters.dateOfBirth
          ? moment(filters.dateOfBirth, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : '',
        customerGender: filters.gender,
        customerStatus: filters.status,
        customerType: filters.type,
        size: 10,
        page: page,
      };

      if (filters.queryBy === 'id' && filters.id) {
        params.id = filters.id;
      } else if (filters.queryBy === 'parentId' && filters.parentId) {
        params.parentId = filters.parentId;
      }

      const { data } = await ApiService.query('/v1/customers', { params });
      const { items = [], totalItems: total = 0, totalPages: pages = 0 } =
        data.data || {};

      customers.value = items;
      totalItems.value = total;
      totalPages.value = pages;
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const deleteCustomer = async (customerId) => {
    const res = await ApiService.delete(`/v1/customers/${customerId}`);

    // ApiService.delete() catches and returns the axios error object instead of throwing
    if (res?.response) {
      const msg =
        res?.response?.data?.message ||
        res?.response?.data?.data?.message ||
        res?.message ||
        'Không thể xóa khách hàng';
      makeToastFaile(msg);
      return false;
    }

    makeToastSuccess('Xóa thành công');
    loadCustomers(currentPage.value);
    return true;
  };

  /**
   * Cập nhật trạng thái khách hàng: 1 = Đang giao dịch, 2 = Ngưng giao dịch
   * Gọi API riêng PATCH /v1/customers/:id/status để tách biệt khỏi update form.
   */
  const updateCustomerStatus = async (customerId, newStatus) => {
    try {
      await ApiService.patch(`/v1/customers/${customerId}/status`, { status: newStatus });
      makeToastSuccess(
        newStatus === CUSTOMER_STATUS.ACTIVE ? 'Đã kích hoạt giao dịch' : 'Đã ngưng giao dịch',
      );
      loadCustomers(currentPage.value);
      return true;
    } catch (error) {
      console.error('Failed to update customer status:', error);
      makeToastFaile('Không thể cập nhật trạng thái');
      return false;
    }
  };

  const applyFilters = () => {
    loadCustomers(1);
  };

  const resetFilters = () => {
    filters.name = '';
    filters.taxCode = '';
    filters.phone = '';
    filters.dateOfBirth = '';
    filters.gender = null;
    filters.status = null;
    filters.type = null;
    filters.parentId = null;
    filters.parentName = '';
    filters.id = null;
    filters.queryBy = null;

    loadCustomers(1);
  };

  const paginationLink = (pageNum) => {
    return pageNum === 1 ? '?' : `?page=${pageNum}`;
  };

  loadCustomers(1);

  return {
    customers,
    totalItems,
    totalPages,
    currentPage,
    isLoading,
    filters,
    loadCustomers,
    deleteCustomer,
    updateCustomerStatus,
    applyFilters,
    resetFilters,
    paginationLink,
  };
}
